import { useEffect, useState } from 'react';
import { api, type FilterCondition } from '../api';
import { OPERATORS } from '../config';

interface Segment {
  id: string;
  name: string;
}
interface Campaign {
  id: string;
  name: string;
  status: string;
  subject: string | null;
  segmentId: string | null;
}
interface Report {
  recipients: number;
  counts: Record<string, number>;
}

const CONTACT_FIELDS = [
  { key: 'fullName', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' },
  { key: 'region', label: 'Region' },
  { key: 'lifecycleStage', label: 'Lifecycle' },
];

export function CampaignsPage({ canSend }: { canSend: boolean }) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [report, setReport] = useState<(Report & { id: string }) | null>(null);

  // Segment builder
  const [segName, setSegName] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([{ field: 'region', operator: 'eq', value: '' }]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  // Campaign composer
  const [cName, setCName] = useState('');
  const [cSegment, setCSegment] = useState('');
  const [cFromName, setCFromName] = useState('');
  const [cFromAddr, setCFromAddr] = useState('');
  const [cSubject, setCSubject] = useState('');
  const [cBody, setCBody] = useState('<p>Hello!</p>');
  const [msg, setMsg] = useState<string | null>(null);

  async function loadAll() {
    setSegments(await api.get<Segment[]>('/api/segments'));
    setCampaigns(await api.get<Campaign[]>('/api/campaigns'));
  }
  useEffect(() => {
    void loadAll();
  }, []);

  const definition = () => ({ logic: 'and', conditions: conditions.filter((c) => c.field) });

  async function preview() {
    const r = await api.post<{ count: number }>('/api/segments/preview', { definition: definition() });
    setPreviewCount(r.count);
  }
  async function createSegment() {
    if (!segName.trim()) return;
    await api.post('/api/segments', { name: segName, definition: definition() });
    setSegName('');
    await loadAll();
  }

  async function createCampaign() {
    if (!cName.trim()) return;
    await api.post('/api/campaigns', {
      name: cName,
      segmentId: cSegment || undefined,
      fromName: cFromName || undefined,
      fromAddress: cFromAddr || undefined,
      subject: cSubject || undefined,
      bodyHtml: cBody,
    });
    setCName('');
    setMsg('Campaign created.');
    await loadAll();
  }

  async function testSend(c: Campaign) {
    const to = window.prompt('Send a test to which email?');
    if (!to) return;
    try {
      await api.post(`/api/campaigns/${c.id}/test`, { to });
      setMsg(`Test sent to ${to}.`);
    } catch {
      setMsg('Test send failed (SES not configured until AWS creds are set).');
    }
  }
  async function send(c: Campaign) {
    if (!window.confirm(`Send campaign "${c.name}" to its full segment?`)) return;
    try {
      const r = await api.post<{ queued: number }>(`/api/campaigns/${c.id}/send`, {});
      setMsg(`Queued ${r.queued} recipient(s).`);
      await loadAll();
    } catch {
      setMsg('Send failed (needs a segment, and SES/Redis configured).');
    }
  }
  async function openReport(c: Campaign) {
    const r = await api.get<Report>(`/api/campaigns/${c.id}/report`);
    setReport({ ...r, id: c.id });
  }

  return (
    <div className="card">
      <h1>Campaigns</h1>
      {!canSend && <p className="muted">You can view campaigns. Sending requires the campaign.send permission.</p>}
      {msg && <p style={{ color: '#1d4ed8' }}>{msg}</p>}

      {/* Segments */}
      <h2 style={{ fontSize: '1.05rem' }}>Audience segments</h2>
      <div className="filters stack">
        {conditions.map((c, i) => (
          <div className="row" key={i}>
            <select value={c.field} onChange={(e) => setConditions((p) => p.map((x, idx) => idx === i ? { ...x, field: e.target.value } : x))}>
              {CONTACT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <select value={c.operator} onChange={(e) => setConditions((p) => p.map((x, idx) => idx === i ? { ...x, operator: e.target.value } : x))}>
              {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input value={(c.value as string) ?? ''} onChange={(e) => setConditions((p) => p.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} placeholder="value" />
            <button className="btn" onClick={() => setConditions((p) => p.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
        <div className="row">
          <button className="btn" onClick={() => setConditions((p) => [...p, { field: 'status', operator: 'eq', value: '' }])}>+ Condition</button>
          <button className="btn" onClick={() => void preview()}>Preview count</button>
          {previewCount !== null && <span className="muted">{previewCount} contact(s) match</span>}
        </div>
        {canSend && (
          <div className="row">
            <input placeholder="Segment name" value={segName} onChange={(e) => setSegName(e.target.value)} />
            <button className="btn btn-primary" onClick={() => void createSegment()}>Save segment</button>
          </div>
        )}
      </div>

      {/* Campaign composer */}
      {canSend && (
        <>
          <h2 style={{ fontSize: '1.05rem' }}>New campaign</h2>
          <div className="stack">
            <input placeholder="Campaign name" value={cName} onChange={(e) => setCName(e.target.value)} />
            <select value={cSegment} onChange={(e) => setCSegment(e.target.value)}>
              <option value="">Select segment…</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="row">
              <input placeholder="From name" value={cFromName} onChange={(e) => setCFromName(e.target.value)} />
              <input placeholder="From address" value={cFromAddr} onChange={(e) => setCFromAddr(e.target.value)} />
            </div>
            <input placeholder="Subject" value={cSubject} onChange={(e) => setCSubject(e.target.value)} />
            <textarea placeholder="HTML body" value={cBody} onChange={(e) => setCBody(e.target.value)} />
            <button className="btn btn-primary" onClick={() => void createCampaign()}>Create campaign</button>
          </div>
        </>
      )}

      {/* Campaign list */}
      <h2 style={{ fontSize: '1.05rem' }}>All campaigns</h2>
      <table className="grid">
        <thead><tr><th>Name</th><th>Status</th><th>Subject</th><th></th></tr></thead>
        <tbody>
          {campaigns.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{c.status}</td>
              <td>{c.subject ?? '—'}</td>
              <td className="row">
                <button className="btn" onClick={() => void openReport(c)}>Report</button>
                {canSend && <button className="btn" onClick={() => void testSend(c)}>Test</button>}
                {canSend && <button className="btn btn-primary" onClick={() => void send(c)}>Send</button>}
              </td>
            </tr>
          ))}
          {campaigns.length === 0 && <tr><td colSpan={4} className="empty">No campaigns yet.</td></tr>}
        </tbody>
      </table>

      {report && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h2 style={{ fontSize: '1.05rem' }}>Report</h2>
          <div className="stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">recipients</span><span>{report.recipients}</span>
            </div>
            {Object.entries(report.counts).map(([k, v]) => (
              <div className="row" key={k} style={{ justifyContent: 'space-between' }}>
                <span className="muted">{k}</span><span>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
